import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";

import type { LegalDoc } from "@/lib/legal/terms";

/**
 * Renders a legal document in German or English only.
 *
 * The rest of the site is available in seven languages, but contracts are not
 * translated by machine here on purpose: a mistranslated liability or
 * jurisdiction clause creates the exact ambiguity the governing-law clause
 * exists to remove, and divergent versions of a contract eventually contradict
 * each other. German is authoritative; English is provided for convenience.
 *
 * Readers in the other five languages are shown the English version together
 * with a notice explaining why — which is more respectful than silently serving
 * them a language they did not choose.
 */
export function LegalDocument({ de, en }: { de: LegalDoc; en: LegalDoc }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language ?? "en").toLowerCase();
  const isGerman = lang.startsWith("de");
  const doc = isGerman ? de : en;
  // Anyone whose UI is neither German nor English is reading a fallback.
  const isFallback = !isGerman && !lang.startsWith("en");

  return (
    <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="font-serif text-[32px] sm:text-[40px] tracking-[-0.03em] text-foreground">
          {doc.title}
        </h1>
        <span className="text-[12px] font-mono text-muted-foreground">
          {isGerman ? "Fassung" : "Version"} {doc.version} · {doc.effective}
        </span>
      </div>

      <p className="mt-4 text-[15.5px] leading-[1.7] text-muted-foreground">{doc.intro}</p>

      {isFallback && (
        <div className="mt-6 flex items-start gap-2.5 rounded-2xl hairline bg-cream p-4">
          <Languages className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
          <p className="text-[13.5px] leading-[1.6] text-muted-foreground">
            This document exists in German and English only. Legal texts are not machine-translated
            here: a mistranslated clause would create exactly the ambiguity a contract is meant to
            remove. The German version is authoritative.
          </p>
        </div>
      )}

      <div className="mt-10 text-[15.5px] leading-[1.75] text-muted-foreground">
        {doc.sections.map((s) => (
          <section key={s.h2} className="mb-9">
            <h2 className="font-serif text-[24px] sm:text-[26px] text-foreground mb-3 tracking-[-0.025em]">
              {s.h2}
            </h2>
            {s.body.map((p, i) => (
              <p key={i} className="mb-2.5 last:mb-0">
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
